import React, { createContext, useContext, useState, useCallback } from 'react';

import { AssignmentConfirmationJobGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

type JobState = 'inProgress' | 'success' | 'error';
type ReceivableReconciliationAlertContextValue = {
  jobState: JobState | null;
  jobType: Omit<
    AssignmentConfirmationJobGQLType['jobType'],
    'assignment'
  > | null;
  receivableCountForConfirmationJob: number | null;
  canRetryUpload: boolean;
  showInProgressAlert: (
    reconciliationJobType: Omit<
      AssignmentConfirmationJobGQLType['jobType'],
      'assignment'
    >,
  ) => void;
  showSuccessAlert: (
    reconciliationJobType: Omit<
      AssignmentConfirmationJobGQLType['jobType'],
      'assignment'
    >,
    receivableCount: number,
    canRetryLoanTapeUpload: boolean,
  ) => void;
  showErrorAlert: (
    reconciliationJobType: Omit<
      AssignmentConfirmationJobGQLType['jobType'],
      'assignment'
    >,
  ) => void;
  clearAlert: () => void;
};

const ReceivableReconciliationAlertContext = createContext<null | ReceivableReconciliationAlertContextValue>(
  null,
);

export const useReceivableReconciliationAlertContext = () => {
  const context = useContext(ReceivableReconciliationAlertContext);

  if (!context) {
    throw new Error(
      'ReceivableReconciliationAlertProvider must be present to use ReceivableReconciliationAlertContext',
    );
  }

  return context;
};

export const ReceivableReconciliationAlertProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [jobState, setJobState] = useState<JobState | null>(null);
  const [jobType, setJobType] = useState<Omit<
    AssignmentConfirmationJobGQLType['jobType'],
    'assignment'
  > | null>(null);
  const [
    receivableCountForConfirmationJob,
    setReceivableCountForConfirmationJob,
  ] = useState<number | null>(null);
  const [canRetryUpload, setCanRetryUpload] = useState<boolean>(false);

  const showInProgressAlert = useCallback(
    (
      reconciliationJobType: Omit<
        AssignmentConfirmationJobGQLType['jobType'],
        'assignment'
      >,
    ) => {
      setJobState('inProgress');
      setJobType(reconciliationJobType);
      setReceivableCountForConfirmationJob(null);
      setCanRetryUpload(false);
    },
    [],
  );

  const showSuccessAlert = useCallback(
    (
      reconciliationJobType: Omit<
        AssignmentConfirmationJobGQLType['jobType'],
        'assignment'
      >,
      receivableCount: number,
      canRetryLoanTapeUpload: boolean,
    ) => {
      setJobState('success');
      setJobType(reconciliationJobType);
      setReceivableCountForConfirmationJob(receivableCount);
      setCanRetryUpload(canRetryLoanTapeUpload);
    },
    [],
  );

  const showErrorAlert = useCallback(
    (
      reconciliationJobType: Omit<
        AssignmentConfirmationJobGQLType['jobType'],
        'assignment'
      >,
    ) => {
      setJobState('error');
      setJobType(reconciliationJobType);
      setReceivableCountForConfirmationJob(null);
      setCanRetryUpload(false);
    },
    [],
  );

  const clearAlert = () => {
    setJobState(null);
    setJobType(null);
    setReceivableCountForConfirmationJob(null);
    setCanRetryUpload(false);
  };

  const context = {
    jobState,
    jobType,
    receivableCountForConfirmationJob,
    canRetryUpload,
    showInProgressAlert,
    showSuccessAlert,
    showErrorAlert,
    clearAlert,
  };

  return (
    <ReceivableReconciliationAlertContext.Provider value={context}>
      {children}
    </ReceivableReconciliationAlertContext.Provider>
  );
};
